import React from 'react';
import {Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {
    Building2,
    School,
    UserCog,
    Activity,
    ChevronRight,
    Users,
    Gamepad2,
    ArrowRight,
    Clock,
    Shield,
} from 'lucide-react';
import {supabase} from '@/services/supabase';
import {LoadingSpinner} from '@/components/ui';

// Enhanced interface for combined dashboard data
interface DashboardKPI {
    // Combined totals
    totalOrganizations: number;
    totalAccounts: number;
    totalGameHosts: number;
    registeredHosts: number;
    pendingInvitations: number;
    totalGameInstances: number;
    totalAdmins: number;

    // Academic breakdown
    academicOrganizations: number;
    academicAccounts: number;
    academicGameHosts: number;
    academicRegisteredHosts: number;

    // Business breakdown
    businessOrganizations: number;
    businessAccounts: number;
    businessGameHosts: number;
    businessRegisteredHosts: number;
}

// Component for individual metric cards
interface MetricCardProps {
    value: number;
    label: string;
    icon: React.ReactNode;
    color: string;
    link?: string;
    subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({value, label, icon, color, link, subtitle}) => {
    const CardContent = (
        <div className="group cursor-pointer">
            <div
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:scale-[1.02] transition-all duration-200">
                <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center text-white`}>
                        {icon}
                    </div>
                    {link && (
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors"/>
                    )}
                </div>
                <div className="space-y-1">
                    <div className="text-2xl font-semibold text-gray-900">
                        {value.toLocaleString()}
                    </div>
                    <div className="text-sm font-medium text-gray-600">
                        {label}
                    </div>
                    {subtitle && (
                        <div className="text-xs text-gray-400">
                            {subtitle}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return link ? <Link to={link}>{CardContent}</Link> : CardContent;
};

// Component for hierarchy visualization
interface HierarchyCardProps {
    type: 'academic' | 'business';
    data: {
        organizations: number;
        accounts: number;
        hosts: number;
        registeredHosts: number;
    };
}

const HierarchyCard: React.FC<HierarchyCardProps> = ({type, data}) => {
    const config = {
        academic: {
            title: 'Academic Structure',
            color: 'from-blue-500 to-blue-600',
            accent: 'bg-blue-100 text-blue-700',
            levels: [
                {
                    name: 'Districts',
                    value: data.organizations,
                    icon: <Building2 className="w-5 h-5"/>,
                    link: '/organizations?type=academic'
                },
                {
                    name: 'Schools',
                    value: data.accounts,
                    icon: <School className="w-5 h-5"/>,
                    link: '/accounts?type=academic'
                },
                {
                    name: 'Hosts',
                    value: data.hosts,
                    icon: <UserCog className="w-5 h-5"/>,
                    link: '/game-hosts?type=academic'
                }
            ]
        },
        business: {
            title: 'Business Structure',
            color: 'from-purple-500 to-purple-600',
            accent: 'bg-purple-100 text-purple-700',
            levels: [
                {
                    name: 'Tier 1',
                    value: data.organizations,
                    icon: <Building2 className="w-5 h-5"/>,
                    link: '/organizations?type=business'
                },
                {
                    name: 'Tier 2',
                    value: data.accounts,
                    icon: <School className="w-5 h-5"/>,
                    link: '/accounts?type=business'
                },
                {
                    name: 'Hosts',
                    value: data.hosts,
                    icon: <UserCog className="w-5 h-5"/>,
                    link: '/game-hosts?type=business'
                }
            ]
        }
    };

    const current = config[type];

    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            {/* Header */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">{current.title}</h3>
                <div className="text-sm text-gray-500 mt-1">
                    {data.registeredHosts} active hosts
                </div>
            </div>

            {/* Hierarchy Flow */}
            <div className="space-y-4">
                {current.levels.map((level, index) => (
                    <div key={level.name}>
                        <Link
                            to={level.link}
                            className="group flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <div
                                    className={`w-10 h-10 rounded-lg ${current.accent} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                                    {level.icon}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {level.value.toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {level.name}
                                    </div>
                                </div>
                            </div>
                            <ChevronRight
                                className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"/>
                        </Link>

                        {/* Connection Arrow */}
                        {index < current.levels.length - 1 && (
                            <div className="flex justify-center mt-3 mb-1">
                                <ArrowRight className="w-4 h-4 text-gray-300"/>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const Dashboard: React.FC = () => {
    // Fetch dashboard KPIs - removed account type dependency
    const {data: dashboardKPI, isLoading} = useQuery({
        queryKey: ['dashboard-kpi-combined'],
        queryFn: async (): Promise<DashboardKPI> => {
            // Fetch all counts in parallel - remove account_type filters
            const [
                orgsResult,
                accountsResult,
                hostsResult,
                gameInstancesResult,
                adminsResult,
                // Academic breakdowns
                academicOrgsResult,
                academicAccountsResult,
                academicHostsResult,
                // Business breakdowns
                businessOrgsResult,
                businessAccountsResult,
                businessHostsResult
            ] = await Promise.all([
                // Combined totals (all account types)
                supabase
                    .from('organizations')
                    .select('id', {count: 'exact', head: true}),
                supabase
                    .from('accounts')
                    .select('id', {count: 'exact', head: true}),
                supabase
                    .from('game_hosts')
                    .select('id, invitation_status', {count: 'exact'})
                    .is('deleted_at', null),
                supabase
                    .from('game_instances')
                    .select('id', {count: 'exact', head: true})
                    .eq('active', true),
                supabase
                    .from('admin_users')
                    .select('id', {count: 'exact', head: true})
                    .eq('status', 'active'),

                // Academic breakdowns
                supabase
                    .from('organizations')
                    .select('id', {count: 'exact', head: true})
                    .eq('account_type', 'academic'),
                supabase
                    .from('accounts')
                    .select('id', {count: 'exact', head: true})
                    .eq('account_type', 'academic'),
                supabase
                    .from('game_hosts')
                    .select('id, invitation_status', {count: 'exact'})
                    .eq('account_type', 'academic')
                    .is('deleted_at', null),

                // Business breakdowns
                supabase
                    .from('organizations')
                    .select('id', {count: 'exact', head: true})
                    .eq('account_type', 'business'),
                supabase
                    .from('accounts')
                    .select('id', {count: 'exact', head: true})
                    .eq('account_type', 'business'),
                supabase
                    .from('game_hosts')
                    .select('id, invitation_status', {count: 'exact'})
                    .eq('account_type', 'business')
                    .is('deleted_at', null),
            ]);

            const hosts = hostsResult.data || [];
            const academicHosts = academicHostsResult.data || [];
            const businessHosts = businessHostsResult.data || [];

            return {
                // Combined totals
                totalOrganizations: orgsResult.count || 0,
                totalAccounts: accountsResult.count || 0,
                totalGameHosts: hostsResult.count || 0,
                registeredHosts: hosts.filter(h => h.invitation_status === 'accepted').length,
                pendingInvitations: hosts.filter(h => h.invitation_status === 'pending' || h.invitation_status === 'sent').length,
                totalGameInstances: gameInstancesResult.count || 0,
                totalAdmins: adminsResult.count || 0,

                // Academic breakdown
                academicOrganizations: academicOrgsResult.count || 0,
                academicAccounts: academicAccountsResult.count || 0,
                academicGameHosts: academicHostsResult.count || 0,
                academicRegisteredHosts: academicHosts.filter(h => h.invitation_status === 'accepted').length,

                // Business breakdown
                businessOrganizations: businessOrgsResult.count || 0,
                businessAccounts: businessAccountsResult.count || 0,
                businessGameHosts: businessHostsResult.count || 0,
                businessRegisteredHosts: businessHosts.filter(h => h.invitation_status === 'accepted').length,
            };
        },
        staleTime: 30 * 1000, // 30 seconds
        refetchInterval: 60 * 1000, // Refetch every minute
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-light text-gray-900 mb-3">
                        Game Management
                    </h1>
                    <p className="text-lg text-gray-500 font-light">
                        Academic & Business Platform Overview
                    </p>
                </div>

                {/* Key Metrics - Top Row (Static Overview) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    <MetricCard
                        value={dashboardKPI?.totalOrganizations || 0}
                        label="Total Organizations"
                        icon={<Building2 className="w-6 h-6"/>}
                        color="bg-gradient-to-br from-blue-500 to-blue-600"
                        subtitle={`${dashboardKPI?.academicOrganizations || 0} Districts • ${dashboardKPI?.businessOrganizations || 0} Tier 1s`}
                    />

                    <MetricCard
                        value={dashboardKPI?.totalAccounts || 0}
                        label="Total Accounts"
                        icon={<School className="w-6 h-6"/>}
                        color="bg-gradient-to-br from-green-500 to-green-600"
                        subtitle={`${dashboardKPI?.academicAccounts || 0} Schools • ${dashboardKPI?.businessAccounts || 0} Tier 2s`}
                    />

                    <MetricCard
                        value={dashboardKPI?.registeredHosts || 0}
                        label="Active Hosts"
                        icon={<Users className="w-6 h-6"/>}
                        color="bg-gradient-to-br from-purple-500 to-purple-600"
                        subtitle={`${dashboardKPI?.totalGameHosts || 0} total hosts`}
                    />

                    <MetricCard
                        value={dashboardKPI?.totalGameInstances || 0}
                        label="Game Instances"
                        icon={<Gamepad2 className="w-6 h-6"/>}
                        color="bg-gradient-to-br from-orange-500 to-orange-600"
                        subtitle="Available games"
                    />
                </div>

                {/* Organizational Structure */}
                <div className="mb-12">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-light text-gray-900 mb-2">
                            Organizational Structure
                        </h2>
                        <p className="text-gray-500">
                            Hierarchical overview of your platform
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <HierarchyCard
                            type="academic"
                            data={{
                                organizations: dashboardKPI?.academicOrganizations || 0,
                                accounts: dashboardKPI?.academicAccounts || 0,
                                hosts: dashboardKPI?.academicGameHosts || 0,
                                registeredHosts: dashboardKPI?.academicRegisteredHosts || 0,
                            }}
                        />

                        <HierarchyCard
                            type="business"
                            data={{
                                organizations: dashboardKPI?.businessOrganizations || 0,
                                accounts: dashboardKPI?.businessAccounts || 0,
                                hosts: dashboardKPI?.businessGameHosts || 0,
                                registeredHosts: dashboardKPI?.businessRegisteredHosts || 0,
                            }}
                        />
                    </div>
                </div>

                {/* Platform Metrics */}
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">Platform Overview</h3>
                            <p className="text-gray-500 mt-1">System configuration and status</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <Link
                            to="/games"
                            className="group text-center hover:bg-gray-50 rounded-lg p-4 -m-4 transition-colors"
                        >
                            <div
                                className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-105 transition-transform">
                                <Gamepad2 className="w-8 h-8 text-orange-600"/>
                            </div>
                            <div className="text-2xl font-semibold text-gray-900">
                                {dashboardKPI?.totalGameInstances || 0}
                            </div>
                            <div className="text-sm text-gray-500">Game Instances</div>
                        </Link>

                        <Link
                            to="/admins"
                            className="group text-center hover:bg-gray-50 rounded-lg p-4 -m-4 transition-colors"
                        >
                            <div
                                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-105 transition-transform">
                                <Shield className="w-8 h-8 text-green-600"/>
                            </div>
                            <div className="text-2xl font-semibold text-gray-900">
                                {dashboardKPI?.totalAdmins || 0}
                            </div>
                            <div className="text-sm text-gray-500">Admin Users</div>
                        </Link>

                        <div className="text-center">
                            <div
                                className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Clock className="w-8 h-8 text-amber-600"/>
                            </div>
                            <div className="text-2xl font-semibold text-gray-900">
                                {dashboardKPI?.pendingInvitations || 0}
                            </div>
                            <div className="text-sm text-gray-500">Pending Invitations</div>
                        </div>

                        <div className="text-center">
                            <div
                                className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Activity className="w-8 h-8 text-blue-600"/>
                            </div>
                            <div className="text-2xl font-semibold text-gray-900">
                                {dashboardKPI?.registeredHosts || 0}
                            </div>
                            <div className="text-sm text-gray-500">Active Hosts</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
